import {
  Alert,
  AlertTitle,
  Button,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { rankingApi } from "@bw/shared/api";
import { playerHandleSuffix } from "@bw/shared/components";
import { useAuth } from "@bw/shared/hooks";
import type { RankedMatchResponse } from "@bw/shared/types/generated";
import { useQueries } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Page } from "../../../common/components/Page";
import { queryKeys } from "../../../common/query/queryClient";
import { useMatch } from "../hooks/useMatch";
import { useReportResult } from "../hooks/useReportResult";

type Side = 1 | 2;
import { sidesFor } from "../components/matchSides";
import { matchSourceLabel } from "../components/competeDisplay";

function joinNames(names: string[]): string {
  return names.join(" & ");
}

interface SidePlayer {
  uid: string | null;
  name: string;
  resolved: boolean;
}

function SidePlayers({ players }: { players: SidePlayer[] }) {
  return (
    <Typography component="span">
      {players.map((player, index) => (
        <Typography
          key={`${player.uid ?? "missing"}-${index}`}
          component="span"
        >
          {index > 0 ? " & " : null}
          <Typography component="span">{player.name}</Typography>
          {player.resolved && player.uid ? (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              {` ${playerHandleSuffix(player.uid)}`}
            </Typography>
          ) : null}
        </Typography>
      ))}
    </Typography>
  );
}

export function MatchDetailPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const { user } = useAuth();
  const myUid = user?.uid;
  const matchQ = useMatch(matchId);
  const report = useReportResult();
  const [picked, setPicked] = useState<Side | null>(null);
  const match = matchQ.data;
  const players = match?.players ?? [];
  const ratingQueries = useQueries({
    queries: players.map((uid) => ({
      queryKey: queryKeys.compete.rating(uid),
      queryFn: () => rankingApi.getRating(uid),
      enabled: Boolean(match),
      retry: false,
    })),
  });

  function nameOf(uid: string): string {
    const index = players.indexOf(uid);
    const name = index >= 0 ? ratingQueries[index]?.data?.display_name : null;
    return name?.trim() || "Player";
  }
  function isResolved(uid: string): boolean {
    const index = players.indexOf(uid);
    return Boolean(
      index >= 0 && ratingQueries[index]?.data?.display_name?.trim(),
    );
  }
  function namesFor(uids: string[]): string[] {
    return uids.length ? uids.map(nameOf) : ["—"];
  }
  function playersFor(uids: string[]): SidePlayer[] {
    return uids.length
      ? uids.map((uid) => ({
          uid,
          name: nameOf(uid),
          resolved: isResolved(uid),
        }))
      : [{ uid: null, name: "—", resolved: false }];
  }

  if (matchQ.isLoading || (!match && !matchQ.isError)) {
    return (
      <Page title="Match detail">
        <Typography color="text.secondary" role="status">
          Loading match…
        </Typography>
      </Page>
    );
  }
  if (matchQ.isError || !match) {
    return (
      <Page title="Match detail">
        <Alert severity="error" role="alert">
          Couldn&apos;t load this match. It may not exist, or please try again.
        </Alert>
      </Page>
    );
  }

  const sides = sidesFor(match.players, match.mode);
  const side1Label = joinNames(namesFor(sides.side1));
  const side2Label = joinNames(namesFor(sides.side2));
  const liveStatus = match.status;
  const liveWinnerSide = match.winner_side;
  const finalizedOnLoad = match.status !== "pending";
  const iAlreadyReported =
    match.status === "pending" && Boolean(myUid) && match.reported_by === myUid;
  const showPicker =
    match.can_report_result === true &&
    !finalizedOnLoad &&
    !iAlreadyReported &&
    !report.isSuccess;

  function submit() {
    if (!showPicker || picked == null || !myUid || report.isPending) return;
    report.mutate({
      match_id: match!.match_id,
      reported_by: myUid,
      winner_side: picked,
    });
  }

  function statusBanner(status: string, winnerSide: number | null | undefined) {
    if (status === "completed") {
      const winnerLabel = winnerSide === 2 ? side2Label : side1Label;
      return (
        <Alert severity="success">
          <AlertTitle>Result confirmed</AlertTitle>
          {winnerLabel} is the recorded winner.
        </Alert>
      );
    }
    if (status === "disputed") {
      return (
        <Alert severity="error" role="alert">
          <AlertTitle>Result disputed</AlertTitle>
          The two sides reported different winners.
        </Alert>
      );
    }
    if (status === "invalid") {
      return <Typography role="status">Match invalid</Typography>;
    }
    if (winnerSide == null)
      return (
        <Typography role="status">
          Result pending
        </Typography>
      );
    return (
      <Alert severity="info" role="status">
        <AlertTitle>
          Result submitted — waiting for the other player to confirm
        </AlertTitle>
        {winnerSide === 2 ? side2Label : side1Label} reported as the winner.
        It&apos;s final once the other player confirms.
      </Alert>
    );
  }

  return (
    <Page title="Match detail">
      <Stack spacing={2}>
        {match.source_link ? (
          <Button
            component={RouterLink}
            to={match.source_link.href}
            sx={{ alignSelf: "flex-start" }}
          >
            {match.source_link.label}
          </Button>
        ) : null}
        <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              {matchSourceLabel(match.source)} ·{" "}
              {match.mode === "doubles" ? "Doubles" : "Singles"} match
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ alignItems: { xs: "stretch", sm: "center" } }}
            >
              <Stack sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Side 1
                </Typography>
                <SidePlayers players={playersFor(sides.side1)} />
              </Stack>
              <Typography color="text.secondary">vs</Typography>
              <Stack sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Side 2
                </Typography>
                <SidePlayers players={playersFor(sides.side2)} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        {report.isError ? (
          <Alert severity="error" role="alert">
            That report didn&apos;t go through. You may not be a participant,
            the match may already be final, or the network failed. Please try
            again.
          </Alert>
        ) : null}

        {!showPicker ? (
          statusBanner(liveStatus, liveWinnerSide)
        ) : (
          <Paper component="section" variant="outlined" sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              <Typography component="h2" variant="h6">
                Who won?
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={picked}
                onChange={(_event, value: Side | null) => setPicked(value)}
                aria-label="Winning side"
                sx={{ alignSelf: "flex-start", flexWrap: "wrap" }}
              >
                <ToggleButton value={1} aria-pressed={picked === 1}>
                  Side 1 — {side1Label}
                </ToggleButton>
                <ToggleButton value={2} aria-pressed={picked === 2}>
                  Side 2 — {side2Label}
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                type="button"
                variant="contained"
                disabled={picked == null || report.isPending}
                onClick={submit}
                sx={{ alignSelf: "flex-start" }}
              >
                Submit result
              </Button>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Page>
  );
}

export type { RankedMatchResponse };
